import CheckField from './CheckField.svelte';
import HiddenField from './HiddenField.svelte';
import PasswordField from './PasswordField.svelte';
import PhysicalPropertyField from './PhysicalPropertyField.svelte';
import DateTimeField from './DateTimeField.svelte';
import TimeRangeField from './TimeRangeField.svelte';
import RetypePasswordField from './RetypePasswordField.svelte';
import SelectField from './SelectField.svelte';
import TextField from './TextField.svelte';
import { camelCase, capitalize, deCapitalize, deDiacritics, kebabCase, lowerCase, upperCase } from '../../util/string-util.js';
import { Locale, dictionaries, lookupMessage } from '../../i18n/index.js';
import { getTextFromTranslatable } from '../../i18n/translatable.js';
import { validator, inlineJSONSchema } from '../../schemas/index.js';
import { InputType } from '../../schemas/schema.types.js';
import { extractErrorMessages } from '../../validation/error-message.js';
export var FormFeedbackType;
(function (FormFeedbackType) {
    FormFeedbackType["none"] = "none";
    FormFeedbackType["error"] = "error";
    FormFeedbackType["success"] = "success";
})(FormFeedbackType || (FormFeedbackType = {}));
export const EVT_FEEDBACK = 'feedback';
export const transformations = {
    none: undefined,
    uppercase: (value) => upperCase(typeof value === 'object' ? JSON.stringify(value) : value.toString()),
    lowercase: (value) => lowerCase(typeof value === 'object' ? JSON.stringify(value) : value.toString()),
    capitalize: (value) => capitalize(typeof value === 'object' ? JSON.stringify(value) : value.toString()),
    decapitalize: (value) => deCapitalize(typeof value === 'object' ? JSON.stringify(value) : value.toString()),
    camelcase: (value) => camelCase(typeof value === 'object' ? JSON.stringify(value) : value.toString()),
    kebabcase: (value) => kebabCase(typeof value === 'object' ? JSON.stringify(value) : value.toString()),
    dediacritics: (value) => deDiacritics(typeof value === 'object' ? JSON.stringify(value) : value.toString())
};
export const validate = (formData, schemaId, locale = Locale.en) => {
    const values = formToObject(formData, schemaId);
    const ajvValidator = validator(schemaId);
    if (ajvValidator) {
        const isValid = ajvValidator(values);
        const errors = !isValid && ajvValidator.errors
            ? extractErrorMessages(ajvValidator.errors, ajvValidator.schema, dictionaries[locale ?? Locale.en])
            : {};
        return { valid: isValid, values: values, errors: errors };
    }
    else {
        console.debug(`No validator found for schema id ${schemaId}.`);
    }
    return { valid: true, values: values, errors: undefined };
};
const formToObject = (formData, schemaId) => {
    //console.log('formToObject schemaId=',schemaId);
    //console.log('formToObject ', formData);
    const inlinedSchema = inlineJSONSchema(schemaId);
    const object = {};
    formData.forEach((value, key) => {
        //TODO implement/test for enums, arrays and what not
        //TODO make it work for nested properties
        //        console.log(`key=${key}`);
        const propertySchema = inlinedSchema?.properties[key];
        let coercedValue;
        if (propertySchema?.type === 'number') {
            coercedValue = parseFloat(value);
        }
        else if (propertySchema?.type === 'boolean') {
            coercedValue = value === 'true';
        }
        else {
            coercedValue = value;
        }
        if (!Reflect.has(object, key)) {
            object[key] = coercedValue;
        }
        else {
            if (!Array.isArray(object[key])) {
                object[key] = [object[key]];
            }
            object[key].push(coercedValue);
        }
    });
    return object;
};
export const buildSelectOptions = (control, locale, context) => {
    if (context?.options) {
        if (control.schema.enum) {
            //filter these options for presence in the enum
            return control.schema.enum
                .map((value) => context.options[value?.value ? value.value : value])
                .filter((option) => !!option);
        }
        else {
            //use these options right away
            return Object.values(context.options);
        }
    }
    else {
        if (control.schema.enum) {
            //build options from the enum itself
            const dictionary = dictionaries[locale] ?? dictionaries.en;
            return (control.schema.enum
                .map((_enumEntry, index) => getEnumOption(control.schema, index, locale, dictionary, transformations[control.input.properties?.transform ?? 'none']))
                .filter((option) => option !== undefined));
        }
        else {
            return undefined; //use CheckField's default
        }
    }
};
// export enum InternalInputType {
// 	PhysicalPropertyField = 'PhysicalPropertyField'
// }
// export interface PhysicalPropertyFieldDefinition {
// 	type: InternalInputType.PhysicalPropertyField;
// 	properties?: SelectInputProperties;
// }
export const getField = (type) => {
    switch (type) {
        case InputType.CheckField:
            return CheckField;
        case InputType.Hidden:
            return HiddenField;
        case InputType.PasswordField:
            return PasswordField;
        case InputType.PhysicalPropertyField:
            return PhysicalPropertyField;
        case InputType.DateTimeField:
            return DateTimeField;
        case InputType.TimeRangeField:
            return TimeRangeField;
        case InputType.RetypePasswordField:
            return RetypePasswordField;
        case InputType.SelectField:
            return SelectField;
        case InputType.TextField:
            return TextField;
    }
};
const getEnumOption = (tlmdInputSchema, index, locale, dictionary, transform) => {
    let option = undefined;
    if (tlmdInputSchema && tlmdInputSchema.enum && tlmdInputSchema.enum[index]) {
        const enumOption = tlmdInputSchema.enum[index];
        const value = enumOption.value ?? enumOption;
        option = { value: value, label: '' };
        if (enumOption.label) {
            option.label = getTextFromTranslatable(enumOption.label, locale);
        }
        else if (enumOption.dictionaryKey) {
            const message = lookupMessage(enumOption.dictionaryKey, dictionary, undefined);
            option.label =
                lookupMessage(enumOption.dictionaryKey, dictionary, undefined) ??
                    (typeof value === 'object' ? JSON.stringify(value) : value.toString());
        }
        else {
            option.label = typeof value === 'object' ? JSON.stringify(value) : value.toString();
        }
        if (transform) {
            option.label = transform(option.label);
        }
    }
    return option;
};
